/**
 * Pending email-invite helper — survives across sign-up/verify/onboarding hops.
 *
 * The bug this fixes:
 *   When an invitee clicks an email invite, they land on EmailInviteAccept.
 *   If they're not signed in, we stash their invite token here, then route
 *   them to /signin. They tap "Sign up", create an account, get sent to
 *   /onboarding/verify-email, then /onboarding/family-name — at which
 *   point they create their OWN family, completely orphaning the invite
 *   they were trying to accept.
 *
 *   This helper is the single source of truth for the invite token AND the
 *   target email. Every onboarding step calls `redirectToPendingInvite()`
 *   to peel off the invite before forcing the user into family-creation.
 *
 * Stored:
 *   - `kinly.pending_email_invite`        — the accept token (string)
 *   - `kinly.pending_email_invite_email`  — the target email (string)
 *
 * SessionStorage (not local) on purpose: clears on tab close, doesn't
 * follow the user to a different account.
 */

const TOKEN_KEY = "kinly.pending_email_invite";
const EMAIL_KEY = "kinly.pending_email_invite_email";

export function getPendingInvite() {
  try {
    return {
      token: sessionStorage.getItem(TOKEN_KEY) || null,
      email: sessionStorage.getItem(EMAIL_KEY) || null,
    };
  } catch {
    return { token: null, email: null };
  }
}

export function setPendingInvite({ token, email }) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    if (email) sessionStorage.setItem(EMAIL_KEY, email);
  } catch { /* private mode */ }
}

export function clearPendingInvite() {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
  } catch { /* noop */ }
}

/**
 * Build the URL to send a user to in order to consume their pending invite.
 * Returns null when no invite is pending — caller falls back to its default.
 */
export function pendingInviteUrl() {
  const { token } = getPendingInvite();
  return token ? `/i/accept/${token}` : null;
}
